import React, { useMemo, useState } from "react";

// Line icons for the contact details; inherit colour from the tile they sit in.
const ICON_PROPS = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

const PinIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 21s-7-6.1-7-11.5a7 7 0 0 1 14 0C19 14.9 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

const MailIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path d="M3.5 7l8.5 6 8.5-6" />
  </svg>
);

const PhoneIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M5 4h3.5l1.5 4.5-2.2 1.4a11 11 0 0 0 6.3 6.3l1.4-2.2L20 15.5V19a1.5 1.5 0 0 1-1.6 1.5A16.5 16.5 0 0 1 3.5 5.6 1.5 1.5 0 0 1 5 4z" />
  </svg>
);

const ArrowIcon = () => (
  <svg {...ICON_PROPS} width={18} height={18} strokeWidth={2}>
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    enquiryType: "Home Loan / Mortgage",
    contactMethod: "Phone",
    message: "",
    consent: false,
  });

  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState({ type: "", msg: "" });

  const mapOpenLink =
    "https://www.google.com/maps/place/Expert+Finance/@-36.9646867,174.9010332,1590m/data=!3m2!1e3!4b1!4m6!3m5!1s0x6d0d4d165155d12f:0x9e4121e5b2e5ff7e!8m2!3d-36.9646867!4d174.9036135!16s%2Fg%2F11jzvn2rkp?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoKLDEwMDc5MjA3MUgBUAM%3D";

  // Simple embed (works reliably without needing the long pb= embed string)
  const mapEmbedSrc = useMemo(() => {
    const q = encodeURIComponent(
      "Suite E3, 123 Ormiston Road, Flat Bush, Auckland 2019"
    );
    return `https://maps.google.com/maps?q=${q}&z=17&t=m&output=embed&iwloc=near`;
  }, []);

  const errors = useMemo(() => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Please enter your full name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Please enter a valid email.";
    if (!form.phone.trim()) e.phone = "Please enter your phone number.";
    if (!form.message.trim())
      e.message = "Tell us briefly what you need help with.";
    if (!form.consent)
      e.consent = "Please confirm you consent to being contacted.";
    return e;
  }, [form]);

  const isValid = Object.keys(errors).length === 0;

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setStatus({ type: "", msg: "" });
  };

  const onBlur = (e) =>
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));

  const onSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      fullName: true,
      email: true,
      phone: true,
      message: true,
      consent: true,
    });

    if (!isValid) {
      setStatus({
        type: "error",
        msg: "A few fields need fixing before this can go through.",
      });
      return;
    }

    try {
      setSubmitting(true);

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: form.fullName,
          email: form.email,
          phone: form.phone,
          enquiryType: form.enquiryType,
          contactMethod: form.contactMethod,
          message: form.message,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data?.error || "Failed");

      setStatus({
        type: "success",
        msg: "Thanks, we’ve got your message and we’ll be in touch soon.",
      });

      setForm({
        fullName: "",
        email: "",
        phone: "",
        enquiryType: "Home Loan / Mortgage",
        contactMethod: "Phone",
        message: "",
        consent: false,
      });
      setTouched({});
    } catch (err) {
      setStatus({
        type: "error",
        msg: err.message || "That didn’t send. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Header */}
      <section className="contact-hero">
        <div className="contact-hero-inner">
          <span className="contact-kicker">Contact</span>
          <h1 className="contact-title">Let’s have a chat.</h1>
          <p className="contact-subtitle">
            Tell us what you’re trying to do and we’ll tell you what it’ll take
            to get there.
          </p>
        </div>
      </section>

      {/* Main */}
      <section className="contact-wrap">
        <div className="contact-grid">
          {/* LEFT: Form */}
          <div className="contact-card contact-card-form contact-animate-left">
            <div className="contact-card-header">
              <h2>Send us a message</h2>
              <p>We usually get back to you within one business day.</p>
            </div>

            {status.msg && (
              <div className={`contact-alert ${status.type}`} role="status">
                {status.msg}
              </div>
            )}

            <form className="contact-form" onSubmit={onSubmit} noValidate>
              <div className="contact-row">
                <div className="contact-field">
                  <label htmlFor="fullName">Full name</label>
                  <input
                    id="fullName"
                    name="fullName"
                    value={form.fullName}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder="Your full name"
                    className={
                      touched.fullName && errors.fullName ? "invalid" : ""
                    }
                    autoComplete="name"
                  />
                  {touched.fullName && errors.fullName && (
                    <div className="contact-error">{errors.fullName}</div>
                  )}
                </div>

                <div className="contact-field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder="you@example.com"
                    className={touched.email && errors.email ? "invalid" : ""}
                    autoComplete="email"
                  />
                  {touched.email && errors.email && (
                    <div className="contact-error">{errors.email}</div>
                  )}
                </div>
              </div>

              <div className="contact-row">
                <div className="contact-field">
                  <label htmlFor="phone">Phone</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder="+64..."
                    className={touched.phone && errors.phone ? "invalid" : ""}
                    autoComplete="tel"
                  />
                  {touched.phone && errors.phone && (
                    <div className="contact-error">{errors.phone}</div>
                  )}
                </div>

                <div className="contact-field">
                  <label htmlFor="enquiryType">
                    What do you need help with?
                  </label>
                  <select
                    id="enquiryType"
                    name="enquiryType"
                    value={form.enquiryType}
                    onChange={onChange}
                    onBlur={onBlur}
                  >
                    <option>Home Loan / Mortgage</option>
                    <option>First Home Buyer</option>
                    <option>Refinance / Loan Review</option>
                    <option>Investment Property</option>
                    <option>Business Lending</option>
                    <option>Insurance / Protection</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              {/* Two options read better as a toggle than a dropdown. */}
              <fieldset className="contact-field contact-method">
                <legend>How should we get back to you?</legend>
                <div className="contact-segment">
                  {["Phone", "Email"].map((method) => (
                    <label
                      key={method}
                      className={`contact-segment-option ${
                        form.contactMethod === method ? "active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="contactMethod"
                        value={method}
                        checked={form.contactMethod === method}
                        onChange={onChange}
                      />
                      {method === "Phone" ? <PhoneIcon /> : <MailIcon />}
                      <span>{method}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="contact-field">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder="For example: we are buying our first home and want to know what we can borrow"
                  className={touched.message && errors.message ? "invalid" : ""}
                />
                {touched.message && errors.message && (
                  <div className="contact-error">{errors.message}</div>
                )}
              </div>

              <div className="contact-consent">
                <label
                  className={`contact-check ${
                    touched.consent && errors.consent ? "invalid" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    name="consent"
                    checked={form.consent}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                  <span className="contact-check-box" aria-hidden="true" />
                  <span>
                    I consent to Expert Finance contacting me about my enquiry.
                  </span>
                </label>
                {touched.consent && errors.consent && (
                  <div className="contact-error">{errors.consent}</div>
                )}
              </div>

              <button
                className="contact-submit"
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Send message"}
                {!submitting && <ArrowIcon />}
              </button>

              <p className="contact-reassure">
                No obligation. Just a straight conversation about options.
              </p>
            </form>
          </div>

          {/* RIGHT: Address + Map */}
          <div className="contact-side contact-animate-right">
            <div className="contact-card contact-card-info">
              <p className="contact-info-eyebrow">Prefer to talk?</p>
              <h2>Expert Finance, Auckland</h2>

              <div className="contact-info-block">
                <div className="contact-info-line">
                  <span className="contact-ico">
                    <PinIcon />
                  </span>
                  <span className="contact-info-text">
                    <span className="contact-info-label">Visit</span>
                    Suite E3, 123 Ormiston Road
                    <br />
                    Flat Bush, Auckland 2019
                  </span>
                </div>
                <a
                  className="contact-info-line"
                  href="mailto:info@expertfinance.co.nz"
                >
                  <span className="contact-ico">
                    <MailIcon />
                  </span>
                  <span className="contact-info-text">
                    <span className="contact-info-label">Email</span>
                    info@expertfinance.co.nz
                  </span>
                </a>
                <a className="contact-info-line" href="tel:+6492176765">
                  <span className="contact-ico">
                    <PhoneIcon />
                  </span>
                  <span className="contact-info-text">
                    <span className="contact-info-label">Call</span>
                    09-2176765
                  </span>
                </a>
              </div>

              <a
                className="contact-map-btn"
                href={mapOpenLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Google Maps
                <ArrowIcon />
              </a>
            </div>

            <div className="contact-card contact-card-map">
              <div className="contact-map-frame">
                <iframe
                  title="Expert Finance Map"
                  src={mapEmbedSrc}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
